import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import '@fontsource/archivo/400.css';
import '@fontsource/archivo/500.css';
import '@fontsource/archivo/600.css';
import '@fontsource/archivo/700.css';
import '@fontsource/geist-mono/400.css';

import './styles/tokens.css';
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/pages.css';
import './styles/storefront.css';
import './styles/site.css';
import './styles/polaris.css';
import './styles/theme-dark.css';

import { AppTree, PRERENDERED_ATTR, SEED_SCRIPT_ID } from './Root';
import { primeQueryCache, type QuerySeed } from './lib/query';
import { proxyBase, routerBasename } from './lib/base';
import { installChunkRecovery } from './lib/chunkRecovery';
import { isEmbeddedAdmin } from './lib/embedded';
import { isSitePath } from './pages/site/navigation';
import { preloadSitePage } from './pages/site/pages';

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');

/** The public data a prerendered page was rendered with (server: http/site.ts), or null for the SPA shell. */
function readSeed(root: HTMLElement): QuerySeed | null {
  if (!root.hasAttribute(PRERENDERED_ATTR)) return null;
  try {
    const seed = JSON.parse(document.getElementById(SEED_SCRIPT_ID)?.textContent ?? '') as unknown;
    return seed && typeof seed === 'object' && !Array.isArray(seed) ? (seed as QuerySeed) : null;
  } catch {
    return null;
  }
}

const seed = readSeed(container);
const tree = (
  <StrictMode>
    <BrowserRouter basename={routerBasename()}>
      <AppTree seed={seed} />
    </BrowserRouter>
  </StrictMode>
);

// A code file of a previous release that no longer exists: the public site reloads once per build (this entry file
// names the build); the dashboard, admin and the shopper's personalizer show a reload notice instead.
const onChunkError = installChunkRecovery({
  build: new URL(import.meta.url).pathname,
  autoReload: () => !proxyBase() && !isEmbeddedAdmin() && isSitePath(window.location.pathname),
});
const rootOptions = {
  onUncaughtError: (error: unknown) => {
    if (onChunkError(error)) return;
    if (typeof window.reportError === 'function') window.reportError(error);
    else console.error(error);
  },
};

if (seed) {
  // A page the server rendered: the same data and the page's component loaded first, then hydration.
  primeQueryCache(seed);
  // If the page's code cannot load, the server's HTML stays as it is (readable, not interactive) under the recovery.
  void preloadSitePage(window.location.pathname).then(
    () => hydrateRoot(container, tree, rootOptions),
    (error: unknown) => {
      if (!onChunkError(error)) console.error(error);
    },
  );
} else {
  createRoot(container, rootOptions).render(tree);
}
